/* eslint-disable no-console */
import { Injectable } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';

import { DemoService } from './demo.service';
import { WsClientService } from './ws-client.service';
import { environment } from '../../environments/environment';
import { BaccaratPhase, CardPosition, DealerConsoleAction, FlagStateType } from '../models/communication/baccarat-enums.model';
import {
    BaccaratEventType,
    CardBurnEvent,
    CardDealtEvent,
    ChatMessageEvent,
    DealerAssistTableErrorEvent,
    FlagStateChangeEvent,
    GameStateChangeEvent,
    GameStateEvent,
    LoginChangeEvent,
    PlayerCountChangeEvent
} from '../models/communication/baccarat-events.model';
import {
    BaccaratRequestType,
    DealerActionRequestMessage,
    GetChatMessagesResponseMessage,
    SetFlagStateRequestMessage
} from '../models/communication/baccarat-requests.model';
import { BaccaratState } from '../models/communication/baccarat-state.model';
import { YEventPayload } from '../models/y-protocol.model';

@Injectable({
    providedIn: 'root'
})
export class DealerService extends WsClientService<BaccaratState> {
    private static readonly maxChatMessageDisplayCount = 50;

    // Set to true to enable demo mode
    private demoMode = false;
    private readonly stateSubject = new BehaviorSubject<BaccaratState | undefined>(undefined);
    private state!: BaccaratState;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly demoService: DemoService
    ) {
        super();

        this.getInitData()
            .then(async (state) => {
                this.state = state;
                return this.sendGetChatMessagesRequest();
            })
            .then((chatMessages) => {
                this.state = { ...this.state, chatMessages: chatMessages ?? [] };
                this.stateSubject.next(this.state);

                this.getEventsObservable().subscribe((yEventPayload) => {
                    this.handleYEvent(yEventPayload);
                });
            })
            .catch((error) => {
                console.error('Failed to get initial state', error);
            });
    }

    override async getInitData(): Promise<BaccaratState> {
        const url = await this.determineDemoModeAndGetWsUrl();

        if (url) {
            return super.getInitData(this.appendTimestampToUrl(url));
        } else {
            this.demoService.start();
            return this.demoService.getInitData();
        }
    }

    override getEventsObservable(): Observable<YEventPayload<BaccaratEventType>> {
        return this.demoMode
            ? this.demoService.getEventsObservable()
            : (super.getEventsObservable() as Observable<YEventPayload<BaccaratEventType>>);
    }

    getStateObservable(): Observable<BaccaratState | undefined> {
        return this.stateSubject.asObservable();
    }

    async sendDealerActionRequest(action: DealerConsoleAction): Promise<void> {
        const data: DealerActionRequestMessage = { action };
        await this.sendRequestMessage(BaccaratRequestType.DealerAction, data);
    }

    async sendSetFlagStateRequest(flag: FlagStateType, state: boolean): Promise<void> {
        const data: SetFlagStateRequestMessage = { flag, state };
        await this.sendRequestMessage(BaccaratRequestType.SetFlagState, data);
        this.updateLocalFlagState(flag, state);
    }

    async sendGetChatMessagesRequest(): Promise<ChatMessageEvent[]> {
        if (this.demoMode) {
            return this.demoService.getChatMessages();
        }

        const response: GetChatMessagesResponseMessage = await this.sendRequestMessage<
            BaccaratRequestType.GetChatMessages,
            undefined,
            GetChatMessagesResponseMessage
        >(BaccaratRequestType.GetChatMessages);

        return response.messages;
    }

    /**
     * Loads `ws` query parameter and sets demoMode and returns web socket url.
     */
    private async determineDemoModeAndGetWsUrl(): Promise<string | undefined> {
        const wsParam = (await this.getQueryParams()).get('ws');
        if (wsParam === 'demo' || !wsParam) {
            this.demoMode = true;
        }

        if (this.demoMode) {
            return undefined;
        } else {
            // Return ws URL
            return wsParam === 'dev'
                ? 'wss://rocket.imaxagaming.com:9779/n/00d4fb50b11b0d9d/d52c740a7d741e64/croupier'
                : wsParam || environment.ws.url;
        }
    }

    private async getQueryParams(): Promise<ParamMap> {
        // Wrap call into setTimeout to avoid initial return of undefined!?!
        return new Promise((resolve) => setTimeout(() => resolve(this.route.snapshot.queryParamMap)));
    }

    private appendTimestampToUrl(url: string): string {
        return `${url}${url.endsWith('/') ? '' : '/'}${Date.now()}`;
    }

    private updateLocalFlagState(flag: FlagStateType, state: boolean): void {
        this.state.flagsState[this.flagStateTypeToPropertyName(flag)] = state;
        this.stateSubject.next({ ...this.state });
    }

    private flagStateTypeToPropertyName(
        flag: FlagStateType
    ): 'chatEnabled' | 'roundBetsFree' | 'roundFlagged' | 'supervisorCalled' | 'tableClosingRequested' {
        switch (flag) {
            case FlagStateType.ChatEnabled:
                return 'chatEnabled';
            case FlagStateType.RoundFlagged:
                return 'roundFlagged';
            case FlagStateType.RoundBetsFree:
                return 'roundBetsFree';
            case FlagStateType.TableClosingRequested:
                return 'tableClosingRequested';
            case FlagStateType.SupervisorCalled:
                return 'supervisorCalled';
            default:
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                throw new Error(`Unknown flag: ${flag}`);
        }
    }

    private handleLoginChange(event: LoginChangeEvent): void {
        if (event.new) {
            const { croupierId: id, name } = event.new;
            this.state.croupier = { id, name };
        } else {
            this.state.croupier = undefined;
        }
    }

    private handleGameStateChange(event: GameStateChangeEvent): void {
        this.state.roundState.phase = event.phase;

        if (event.roundId) {
            this.state.roundState.roundId = event.roundId;
        }

        if ([BaccaratPhase.NewRoundPreparation, BaccaratPhase.RoundVoid].includes(event.phase)) {
            this.state = {
                ...this.state,
                tableState: undefined,
                shoeChangeState: undefined,
                flagsState: {
                    chatEnabled: this.state.flagsState.chatEnabled
                }
            };
        } else if (event.phase === BaccaratPhase.DealingCards) {
            this.state.tableState = {
                nextCardPosition: CardPosition.PlayerFirstCard
            };
        }

        if (this.state.tableState) {
            this.state.tableState.outcome = event.outcome;
        }
    }

    private handleGameStateEvent(event: GameStateEvent): void {
        this.state = event.state;
    }

    private handleCardBurn(event: CardBurnEvent): void {
        this.state.shoeChangeState = event;
    }

    private handleCardDealt(event: CardDealtEvent): void {
        const { card, cardPosition, bankerHandValue, playerHandValue, nextCardPosition } = event;
        const cards = { ...(this.state.tableState?.cards || {}), [cardPosition]: card };
        this.state.tableState = { cards, bankerHandValue, playerHandValue, nextCardPosition, outcome: this.state.tableState?.outcome };
    }

    private handleCutCardDrawn(): void {
        this.state.shoeChangeState = { ...this.state.shoeChangeState, cutCardDrawn: true };
    }

    private handleFlagStateChange(event: FlagStateChangeEvent): void {
        this.state.flagsState[this.flagStateTypeToPropertyName(event.flag)] = event.state;
    }

    private handlePlayerCountChange(event: PlayerCountChangeEvent): void {
        this.state.playerCount = event.count;
    }

    private handleDealerAssistTableError(event: DealerAssistTableErrorEvent): void {
        this.state.roundState.errorCode = event.errorCode;
    }

    private handleChatMessage(event: ChatMessageEvent): void {
        const messages = this.state.chatMessages || [];

        this.state.chatMessages =
            messages.length < DealerService.maxChatMessageDisplayCount ? [event, ...messages] : [event, ...messages.slice(0, -1)]; // Remove the oldest message (last element)
    }

    private handleYEvent(yEventPayload: YEventPayload<BaccaratEventType>): void {
        const { data: event, name: baccaratEventType } = yEventPayload;

        switch (baccaratEventType) {
            case BaccaratEventType.LoginChange:
                this.handleLoginChange(event as LoginChangeEvent);
                break;
            case BaccaratEventType.GameStateChange:
                this.handleGameStateChange(event as GameStateChangeEvent);
                break;
            case BaccaratEventType.GameState:
                this.handleGameStateEvent(event as GameStateEvent);
                break;
            case BaccaratEventType.CardBurn:
                this.handleCardBurn(event as CardBurnEvent);
                break;
            case BaccaratEventType.CardDealt:
                this.handleCardDealt(event as CardDealtEvent);
                break;
            case BaccaratEventType.CutCardDrawn:
                this.handleCutCardDrawn();
                break;
            case BaccaratEventType.FlagStateChange:
                this.handleFlagStateChange(event as FlagStateChangeEvent);
                break;
            case BaccaratEventType.PlayerCountChange:
                this.handlePlayerCountChange(event as PlayerCountChangeEvent);
                break;
            case BaccaratEventType.DealerAssistTableError:
                this.handleDealerAssistTableError(event as DealerAssistTableErrorEvent);
                break;
            case BaccaratEventType.ChatMessage:
                this.handleChatMessage(event as ChatMessageEvent);
                break;
        }

        this.stateSubject.next({ ...this.state });
    }
}
